<template>
  <PageHeader>
    <template #header-title>
      {{ $t('Find a freelancer') }}
    </template>
    <template #header-actions>
      <div v-if="showEnterpriseOptions" class="toggle-switch" style="cursor: pointer">
        <label
          :class="{ 'c-gray ': contactsOnly }"
          for="ts1"
          class="ts-label m-r-10 ucase f-13 f-500"
        >
          {{ $t('Marketplace') }}
        </label>
        <input id="ts1" v-model="contactsOnly" type="checkbox" hidden="hidden" />
        <label for="ts1" class="ts-helper" />
        <label
          :class="{ 'c-black': contactsOnly, 'c-gray': !contactsOnly }"
          for="ts1"
          class="ts-label m-l-10 ucase f-13 f-500 m-r-0"
        >
          {{ $t('Trusted Contacts') }}
          <span v-if="contactsOnly" class="circle bgm-lightgray circle20 f-300 m-l-5">
            {{ totalContactsCount }}
          </span>
        </label>
      </div>
    </template>
  </PageHeader>
  <ModalTrustedContactsInvite
    v-if="isCompany || teamRole === 'organisation'"
    ref="inviteModal"
    :contact-ids="profileIds"
  />
  <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
  <notifier-growl />
  <div class="row m-b-15">
    <div class="col-xs-12">
      <InputSearch
        ref="inputBar"
        :ref-search="filter.search"
        :ref-store="{
          store: $store,
          mutator: {
            term: 'SET_SEARCH_TERM',
            searching: 'SEARCHING',
          },
        }"
      >
        <transition name="fade">
          <div
            v-if="filter.search.term && filter.search.term.split(',').length > 1"
            id="search-match"
          >
            <label>
              <div class="radio radio-inline">
                <input
                  v-model="match"
                  type="radio"
                  name="match"
                  value="broad"
                  class="form-control"
                />
                {{ $t('Match any keyword') }}
                <i class="input-helper" />
              </div>
            </label>
            &nbsp;&nbsp;&nbsp;&nbsp;
            <label>
              <div class="radio radio-inline">
                <input
                  v-model="match"
                  type="radio"
                  name="match"
                  value="exact"
                  class="form-control"
                />
                {{ $t('Must match all keywords') }}
                <i class="input-helper" />
              </div>
            </label>
          </div>
        </transition>
      </InputSearch>
    </div>
  </div>
  <div class="row">
    <div class="col-md-3">
      <LeftSelectorBar
        :ref-selectors="[
          engagedProfiles,
          categories,
          organisation,
          experience,
          hourlyRate,
          markets,
        ]"
        :header-class="contactsOnly ? 'bgm-indigo-100 c-black' : ''"
      />
    </div>

    <ListerTemplate :have-elements="!searching" @hit-bottom="loadNextPage">
      <template #header>
        <div v-if="!searching" style="margin-left: 15px; margin-right: 15px">
          <div class="pull-left">
            <p :class="{ 'c-black f-400': contactsOnly }" class="lead">
              {{ profilesCount }}
              {{ contactsOnly ? $t('contacts') : $t('profiles') }}
              {{ $t('found') }}
              <a v-if="contactsOnly" class="btn btn-xs btn-success m-l-10" @click="openInviteModal">
                <span v-tooltip:top="'Invite the contacts in the list to a job'">
                  {{ $t('Invite to job') }}
                </span>
              </a>
            </p>
          </div>

          <div class="pull-right">
            <a
              href="/profiles/liked"
              class="btn btn-default waves-effect"
              style="padding: 4px 12px"
            >
              <span
                class="glyphicon glyphicon-heart"
                style="color: #747487; top: 2px; margin-right: 8px"
              ></span>
              {{ $t('Saved') }}
            </a>
          </div>
        </div>
      </template>

      <div class="list-group lg-odd-black card" style="background: none; margin-bottom: 0">
        <ListAnimation>
          <ListItemCard
            v-for="(profile, index) in profiles"
            :key="profile.id + profilesKey"
            :index="index - indexSubTractor"
            :card="profile"
            :type="enums.cards.profile"
            :avatar-url="profile ? profile.avatar_path : undefined"
            :card-destination-url="profile.url_profile ? `${profile.url_profile}` : undefined"
            :card-title="profile.job_title"
            :card-name="profile.name"
            :show-rate="true"
            :show-likes="permissionToLike"
            :can-contact="permissionToContact"
          />
        </ListAnimation>
      </div>
      <template #footer>
        <div v-if="isCompany && profilesCount === 0 && !searching" class="bgm-white p-20">
          <div v-if="contactsOnly" class="p-20">
            <p class="f-16">
              {{ $t('Your current search filters has no results among your Trusted Contacts.') }}
            </p>
            <p>
              <a href="" class="btn btn-default btn-sm" @click="contactsOnly = false">
                {{ $t('Search in Worksome Marketplace') }}
              </a>
            </p>
          </div>
          <div v-else>
            <p class="f-16">
              {{
                $t(
                  'If you create a job post in Worksome, you will get in touch with the freelancers who can help.',
                )
              }}
            </p>
            <p class="f-16">
              {{ $t('It takes 5 minutes and it is completely free and non-binding.') }}
            </p>
            <a href="/job/create?fs" class="btn btn-sm btn-primary">
              {{ $t('Create a free job posting') }}
            </a>
          </div>
        </div>
        <div
          v-if="hasActiveFilters && (hasAllProfiles || profilesCount === 0 || !awaitingProfiles)"
        >
          <div v-if="profilesCount === 0 && !awaitingProfiles" class="p-20">
            <p class="f-16">
              {{ $t('Your current search found no freelancers.') }}
            </p>
          </div>
          <a href="#" class="iblock p-l-15 m-t-15 f-13 f-500" @click="clearFilter">
            {{ $t('Clear all search filters') }}
          </a>
        </div>
        <div v-else style="margin: 1px 14px 0 14px">
          <DisplaySkeleton />
          <DisplaySkeleton />
          <DisplaySkeleton />
        </div>
      </template>
    </ListerTemplate>
  </div>
</template>

<script>
import { FILTER_MUTATIONS } from '@/state/mutation-types'
import { FILTER_ACTIONS, PROFILES_ACTIONS } from '@/state/action-types'
import enums from '@/utils/enum'
import ModalTrustedContactsInvite from '@/components/ModalTrustedContactsInvite.vue'
import InputSearch from '@/components/InputSearch.vue'
import LeftSelectorBar from '@/components/LeftSelectorBar.vue'
import ListerTemplate from '@/components/ListerTemplate.vue'
import ListAnimation from '@/components/ListAnimation.vue'
import ListItemCard from '@/components/ListItemCard.vue'
import DisplaySkeleton from '@/components/DisplaySkeleton.vue'
import { QueryEncoder } from '@/utils/Encoders'
import PageHeader from '@/components/PageHeader.vue'

export default {
  name: 'App',
  components: {
    PageHeader,
    ModalTrustedContactsInvite,
    InputSearch,
    LeftSelectorBar,
    ListerTemplate,
    ListAnimation,
    ListItemCard,
    DisplaySkeleton,
  },
  data() {
    return {
      oldPath: '', // Used for detection of when to send page views to google analytics
      historyNavigation: false,
    }
  },

  computed: {
    /**
     * This method makes it easy for us to watch the vuex data for state changes
     */
    filter() {
      return this.$store.getters.filter
    },

    showEnterpriseOptions() {
      return (
        this.accountPlan === 'enterprise' &&
        (this.isCompany || this.teamRole === 'organisation') &&
        this.permissionToTalentPool
      )
    },

    hasAllProfiles() {
      return this.$store.getters.hasAllProfiles
    },

    hasActiveFilters() {
      // TODO: Actually check if any filters are active
      return true
    },

    profiles() {
      const { items } = this.$store.getters.profiles
      return items && typeof items !== 'string' ? items : []
    },

    profileIds() {
      return this.profilesCount > 0 ? this.$store.getters.profiles.all_profile_ids : []
    },

    profilesCount() {
      const { profiles } = this.$store.getters
      return parseInt(profiles && profiles.count !== undefined ? profiles.count : 0, 10)
    },

    profilesKey() {
      return this.$store.getters.profilesKey
    },

    isCompany() {
      return this.account.isCompany
    },

    accountPlan() {
      return this.account !== '' ? this.account.plan : 'free'
    },

    awaitingProfiles() {
      return this.$store.getters.profilesSearching
    },

    searching() {
      const search = this.$store.getters.profiles
      return search && typeof search.items === 'string'
    },

    indexSubTractor() {
      const page = this.$store.getters.profiles.page - 1
      return page * 25
    },

    permissionToLike() {
      if (!this.isCompany) {
        return false
      }
      if (typeof currentTeamPermissions === 'object' && currentTeamPermissions.length > 0) {
        return currentTeamPermissions.includes('like-freelancers')
      }
      return !this.userIsTeamMember
    },

    permissionToTalentPool() {
      if (!this.isCompany) {
        return false
      }
      if (typeof currentTeamPermissions === 'object' && currentTeamPermissions.length > 0) {
        return currentTeamPermissions.includes('access-talent-pool')
      }
      return this.teamRole === 'owner' || this.teamRole === 'admin'
    },

    userIsTeamMember() {
      return typeof this.account.team_member === 'object'
    },

    permissionToContact() {
      if (!this.isCompany) {
        return false
      }
      if (typeof currentTeamPermissions === 'object' && currentTeamPermissions.length > 0) {
        return currentTeamPermissions.includes('participate-dialog')
      }
      return !this.userIsTeamMember
    },

    contactsOnly: {
      get() {
        if (!this.showEnterpriseOptions) return false
        return this.$store.getters.filter.own
      },
      set(val) {
        this.$store.commit(FILTER_MUTATIONS.SET_OWN_SELECTED, val)
      },
    },

    match: {
      get() {
        return this.filter.match
      },
      set(val) {
        this.$store.commit(FILTER_MUTATIONS.SET_MATCH_TYPE, val)
      },
    },

    enums() {
      return enums
    },

    markets() {
      return {
        refObj: this.$store.getters.markets,
        title: $t('Markets'),
        children: this.$store.getters.marketRegions,
        type: 'radio',
        name: 'markets',
        collapsable: true,
        mutator: FILTER_MUTATIONS.MARKET_SELECTED,
      }
    },

    categories() {
      const cats = this.$store.getters.categories
      if (cats.refObj.list) {
        cats.refObj.list.unshift({ id: '', name: $t('All') })
      }
      if (
        cats.refObj.selected &&
        typeof cats.refObj.selected === 'object' &&
        cats.refObj.selected.length === 0
      ) {
        cats.refObj.selected = ''
      }
      cats.collapsable = true
      return cats
    },

    organisation() {
      return this.$store.getters.divisions
    },

    experience() {
      return {
        refObj: this.$store.getters.experience,
        title: $t('Experience'),
        type: 'checkbox',
        name: 'experience',
        approved: true,
        collapsable: true,
        startCollapsed:
          !this.$store.getters.experience.selected ||
          this.$store.getters.experience.selected.length === 0,
        mutator: FILTER_MUTATIONS.SET_EXPERIENCE_SELECTED,
      }
    },

    hourlyRate() {
      return {
        refObj: this.$store.getters.rates,
        title: $t(currentRateType === 'daily' ? 'Daily rate' : 'Hourly rate'),
        type: 'radio',
        name: 'rates',
        approved: true,
        collapsable: true,

        mutator: FILTER_MUTATIONS.SET_RATES_SELECTED,
      }
    },

    engagedProfiles() {
      return {
        refObj: this.$store.getters.engaged,
        type: 'radio',
        name: 'engaged',
        title: $t('Engaged freelancers'),
        approved: true,
        collapsable: true,
        startCollapsed: true,
        mutator: FILTER_MUTATIONS.SET_ENGAGED_SELECTED,
        // display : this.account.isCompany
        display: false, // Disabled for now, TODO: Solve getting setting from URL and uncollapse selector
      }
    },

    teamRole() {
      return currentTeamRole || null
    },

    totalContactsCount() {
      return currentUserContactsCount || 0
    },

    hasHires() {
      return this.isCompany && currentUserHasHires
    },
  },

  watch: {
    filter: {
      handler() {
        if (this.$store.state.filter.filterSetupComplete) {
          this.updateQueryParamsFromFilter()
          this.updateProfiles()
        }
      },
      deep: true,
    },
  },

  created() {
    this.oldPath = this.$route.fullPath
    this.getData()
    this.updateFilterParamsFromQuery()
    this.updateQueryParamsFromFilter()
    this.updateProfiles()

    const app = this
    window.onpopstate = () => {
      app.historyNavigation = true
      app.updateFilterParamsFromQuery()
    }
  },

  methods: {
    openInviteModal() {
      this.$refs.inviteModal.triggerTheModal()
    },
    // Gets filter data such as categories and regions
    async getData() {
      try {
        /*
          this.$store.commit(FILTER_MUTATIONS.SET_ENGAGED_LIST, {
            'dialog': this.$t('In conversations'),
            'hired': this.$t('Hired anytime'),
            'contract': this.$t('Hired now'),
            'likes': this.$t('Saved'),
            'hired': this.$t('Hired'),
            '': this.$t('None'),
          })
          this.$store.commit(FILTER_MUTATIONS.SET_ENGAGED_SELECTED, [''])
          */
        await this.$store.dispatch(FILTER_ACTIONS.INITIATE_PROFILES_FILTER)
      } catch (e) {
        this.$store.commit('sendNotification', {
          type: enums.notification.types.ERROR,
          msg: this.$t('An error occurred when getting filter information'),
          sender: this,
          severity: enums.notification.severity.CRITICAL,
        })
      }
    },

    async updateProfiles() {
      try {
        await this.$store.dispatch(PROFILES_ACTIONS.SEARCH_PROFILES)
      } catch (e) {
        // this.$store.commit('sendNotification', {
        //   type: enums.notification.types.ERROR,
        //   msg: this.$t('An error occurred when searching for profiles'),
        //   sender: this,
        //   severity: enums.notification.severity.CRITICAL,
        // })
      }
    },

    async loadNextPage() {
      try {
        this.$store.dispatch(PROFILES_ACTIONS.NEXT_PAGE).then()
      } catch (e) {
        this.$store.commit('sendNotification', {
          type: enums.notification.types.ERROR,
          msg: this.$t('An error occurred when getting profiles'),
          sender: this,
          severity: enums.notification.severity.CRITICAL,
        })
      }
    },

    /**
     * Method that updates selected filter parts from current query in the browser
     */
    updateFilterParamsFromQuery() {
      // We get the query and the filter
      const query = QueryEncoder.decodeQueryIntoObject(window.location.search)
      this.$store.dispatch(FILTER_ACTIONS.UPDATE_FILTER_FROM_QUERY, query)
    },

    clearFilter() {
      const q = {
        s: '',
        area: '',
        category_id: '',
        rate: '',
        exp: '',
        match: '',
        market: '',
        engaged: '',
        divisions: '',
      }
      this.$store.dispatch(FILTER_ACTIONS.UPDATE_FILTER_FROM_QUERY, q)
      this.updateQueryParamsFromFilter()
    },
    /**
     * Method that updates the current query parameters from our filter
     */
    updateQueryParamsFromFilter() {
      const q = {} // We create a new query
      // s=
      // &rating=
      // &exp=
      // &rate=
      // &hours=
      // &areas=
      // &market=dk
      // &match=broad
      // &page=1
      // &category_id=1
      // Vuex data sets the query in the users browser
      const { filter } = this
      q.s = filter.search.term
      q.area = filter.regions.selected ? filter.regions.selected.join() : ''
      q.category_id = filter.categories.selected
      q.rate = filter.rates.selected
      q.exp = filter.experience.selected ? filter.experience.selected.join('|') : ''
      q.match = filter.match
      // q.engaged = filter.engaged.selected ? filter.engaged.selected.join() : ''
      q.engaged = filter.engaged !== undefined ? filter.engaged.selected : ''
      // q.division_ids = filter.division_ids.selected

      if (this.showEnterpriseOptions && filter.own.toggle) {
        q.own = filter.own ? 1 : 0
        q.division = filter.divisions.selected ? filter.divisions.selected.join() : ''
        // TODO: Variable that determins if we display the switch selector, should also determine, if it is added to the url
      }

      for (const i in q) {
        if (!q[i]) {
          q[i] = ''
        }
      }

      // Update url and send a pageview
      if (!this.historyNavigation) {
        // catch because in our hooks we sometimes trigger this and attempt
        // to push the same query to the router
        this.$router.replace({ path: window.location.pathname, query: q })
      }
      this.historyNavigation = false
      if (typeof window.gtag === 'function' && this.oldPath !== this.$route.fullPath) {
        window.gtag('event', 'page_view', {
          pageview: window.location.pathname,
        }) // We send a new pageview to google analytics
        this.oldPath = this.$route.fullPath
      }
    },
  },
}
</script>

<style scoped lang="scss">
#ProfilesNumberLoader {
  position: relative;
  display: inline-block;
  p {
    margin-left: 30px;
  }
  svg {
    width: 25px;
    height: 25px;
    margin: 5px 0 0 0;
    circle {
      stroke: #2196f3;
      animation: dash 1.5s ease-in-out infinite;
    }

    @media screen and (max-width: 768px) {
      width: 20px;
      height: 20px;
      margin-top: 0px;
    }
  }
}
</style>
